import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, Trash2, Pencil, Flag, CalendarDays, Images, BookOpen, MapPin, Tent, ScrollText } from "lucide-react";
import { MemberAvatar } from "./MemberAvatar";
import { ReportDialog, ConfirmDialog } from "./FriendDialogs";
import { socialApi, memberApi, errorText, WALLPAPER } from "../../lib/member";

export function useBlob(path) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    setSrc(null);
    if (!path) return undefined;
    let url = null, gone = false;
    socialApi.blob(path).then(b => { if (!gone) { url = URL.createObjectURL(b); setSrc(url); } }).catch(() => setSrc(null));
    return () => { gone = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return src;
}

const ICON = { album: Images, plan: CalendarDays, experience: Tent, favorite: MapPin, recipe: BookOpen, heirloom: ScrollText };
export const LABEL = { photo: "Photo", album: "Shared album", plan: "Calendar plan", experience: "Farm experience", favorite: "Saved place", recipe: "Recipe", heirloom: "Heirloom recipe" };

function PhotoBody({ it }) {
  const src = useBlob(it.media);
  return (<>
    <div className="aspect-[4/3] bg-black/5 overflow-hidden">{src && <img src={src} alt={it.caption || "Photo"} className="w-full h-full object-cover" />}</div>
    {it.caption && <p className="text-sm mt-3" style={{ overflowWrap: "anywhere" }}>{it.caption}</p>}
  </>);
}

// `preview` (draft preview and the owner's editor) renders titles as plain text so nothing navigates away.
export function ItemBody({ it, preview }) {
  if (it.type === "photo") return <PhotoBody it={it} />;
  const Icon = ICON[it.type];
  return (
    <div>
      <div className="text-[11px] tracking-widest uppercase fs-accent mb-2 inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" aria-hidden="true" />{LABEL[it.type]}</div>
      <p className="fs-serif text-lg" style={{ overflowWrap: "anywhere" }}>{preview || !it.link ? it.title : <Link to={it.link} className="underline underline-offset-4">{it.title}</Link>}</p>
      {(it.date || it.subtitle) && <p className="text-xs fs-text-2 mt-1">{it.date || it.subtitle}</p>}
    </div>
  );
}

function Comment({ c, it, reload, onReport }) {
  const [edit, setEdit] = useState(null); const [confirm, setConfirm] = useState(null);
  const save = async () => { try { await socialApi.patch(`/profile/items/${it.id}/comments/${c.id}`, { text: edit }); setEdit(null); reload(); } catch (e) { toast.error(errorText(e)); } };
  const run = async () => { try { await socialApi.del(`/profile/items/${it.id}/comments/${c.id}`); reload(); } catch (e) { toast.error(errorText(e)); } };
  const del = () => setConfirm({ title: "Delete this comment?", body: "It will be removed for everyone who can see this item.", cta: "Delete", run });
  if (edit !== null) return <div className="flex gap-2"><input value={edit} maxLength={1000} onChange={e => setEdit(e.target.value)} className="fs-input flex-1 min-w-0 border px-3 py-2 text-sm bg-transparent" data-testid={`fp-comment-edit-input-${c.id}`} /><button className="fs-btn" onClick={save} data-testid={`fp-comment-edit-save-${c.id}`}>Save</button><button className="fs-btn-2" onClick={() => setEdit(null)} data-testid={`fp-comment-edit-cancel-${c.id}`}>Cancel</button></div>;
  return (
    <div className="text-sm flex gap-2 items-start" data-testid={`fp-comment-${c.id}`}>
      <span className="min-w-0 flex-1" style={{ overflowWrap: "anywhere" }}><span className="fs-serif">{c.author.display_name}</span> {c.text}{c.edited && <span className="text-xs fs-text-2"> (edited)</span>}</span>
      {c.can_edit && <button onClick={() => setEdit(c.text)} aria-label="Edit comment" className="p-1 opacity-60 hover:opacity-100" data-testid={`fp-comment-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
      {c.can_delete && <button onClick={del} aria-label="Delete comment" className="p-1 opacity-60 hover:opacity-100" data-testid={`fp-comment-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
      {c.can_report && <button onClick={() => onReport({ target: "comment", id: c.id, item_id: it.id }, `${c.author.display_name}'s comment`)} aria-label="Report comment" className="p-1 opacity-60 hover:opacity-100" data-testid={`fp-comment-report-${c.id}`}><Flag className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

function Comments({ it, onCount, onReport }) {
  const [list, setList] = useState(null); const [text, setText] = useState(""); const [busy, setBusy] = useState(false);
  const load = useCallback(() => socialApi.get(`/profile/items/${it.id}/comments`).then(d => { setList(d.comments); onCount(d.comments.length); }).catch(e => toast.error(errorText(e))), [it.id, onCount]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!text.trim()) return; setBusy(true);
    try { await socialApi.post(`/profile/items/${it.id}/comments`, { text }); setText(""); await load(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  if (!list) return <p className="text-xs fs-text-2 mt-3">Loading comments…</p>;
  return (
    <div className="mt-3 border-t pt-3 space-y-2" style={{ borderColor: "rgba(15,29,47,.12)" }} data-testid={`fp-comments-${it.id}`}>
      {list.map(c => <Comment key={c.id} c={c} it={it} reload={load} onReport={onReport} />)}
      <div className="flex gap-2"><input value={text} onChange={e => setText(e.target.value)} maxLength={1000} placeholder="Write a comment…" className="fs-input flex-1 min-w-0 border px-3 py-2 text-sm bg-transparent" data-testid={`fp-comment-input-${it.id}`} />
        <button className="fs-btn" disabled={busy || !text.trim()} onClick={send} data-testid={`fp-comment-send-${it.id}`}>Post</button></div>
    </div>
  );
}

function PreviewActions({ it, idx }) {
  const why = "Preview only: likes and comments can't be used here";
  return (
    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs" data-testid="fp-preview-counts">
      <button disabled aria-disabled="true" title={why} className="inline-flex items-center gap-1.5 opacity-50 cursor-not-allowed" data-testid={`fp-preview-like-${idx}`}><Heart className="w-4 h-4" />{it.likes}</button>
      {it.allow_comments ? <button disabled aria-disabled="true" title={why} className="inline-flex items-center gap-1.5 opacity-50 cursor-not-allowed" data-testid={`fp-preview-comments-${idx}`}><MessageCircle className="w-4 h-4" />{it.comments}</button>
        : <span className="fs-text-2" data-testid={`fp-preview-comments-off-${idx}`}>Comments off</span>}
      <span className="fs-text-2 ml-auto" data-testid={`fp-preview-note-${idx}`}>Preview: buttons are inactive</span>
    </div>
  );
}

function ItemCard({ it, idx, preview, onReport }) {
  const [s, setS] = useState({ likes: it.likes, liked: it.liked, comments: it.comments });
  const [open, setOpen] = useState(false);
  useEffect(() => { setS({ likes: it.likes, liked: it.liked, comments: it.comments }); setOpen(false); }, [it.id, it.likes, it.liked, it.comments]);
  const toggle = async () => {
    try { const r = s.liked ? await socialApi.del(`/profile/items/${it.id}/like`) : await socialApi.put(`/profile/items/${it.id}/like`); setS(x => ({ ...x, ...r })); }
    catch (e) { toast.error(errorText(e)); }
  };
  const onCount = useCallback((n) => setS(x => ({ ...x, comments: n })), []);
  return (
    <li className="fs-card p-4" data-testid={`fp-item-${it.id || `${it.type}-${idx}`}`}>
      <ItemBody it={it} preview={preview} />
      {preview ? <PreviewActions it={it} idx={idx} /> : <div className="flex items-center gap-4 mt-3 text-xs">
        <button onClick={toggle} aria-pressed={!!s.liked} aria-label={s.liked ? "Unlike" : "Like"} className="inline-flex items-center gap-1.5" data-testid={`fp-like-${it.id}`}><Heart className="w-4 h-4" fill={s.liked ? "currentColor" : "none"} />{s.likes}</button>
        {it.allow_comments ? <button onClick={() => setOpen(o => !o)} aria-expanded={open} className="inline-flex items-center gap-1.5" data-testid={`fp-comments-toggle-${it.id}`}><MessageCircle className="w-4 h-4" />{s.comments}</button>
          : <span className="fs-text-2" data-testid={`fp-comments-off-${it.id}`}>Comments off</span>}
        <button onClick={() => onReport({ target: "item", id: it.id }, "this item")} className="inline-flex items-center gap-1 opacity-60 hover:opacity-100 ml-auto" data-testid={`fp-item-report-${it.id}`}><Flag className="w-3.5 h-3.5" />Report</button>
      </div>}
      {open && !preview && it.allow_comments && <Comments it={it} onCount={onCount} onReport={onReport} />}
    </li>
  );
}

// The visitor layout. `preview` = owner's unsaved-draft preview: same layout, inactive interactions, owner's own photo.
// The owner's wallpaper (only when they turned it on) frames the whole profile; the surrounding shell stays neutral.
export function FriendProfileView({ data, preview = false, footer = null }) {
  const m = data.member; const art = data.wallpaper_theme && WALLPAPER[data.wallpaper_theme];
  const [rep, setRep] = useState(null);
  const onReport = useCallback((body, label) => setRep({ body, display_name: label }), []);
  const submit = async ({ reason, details }) => {
    try { await socialApi.post("/profile/report", { ...rep.body, reason, details }); toast.success("Report sent to the American Farmstand team"); return true; }
    catch (e) { toast.error(errorText(e)); return false; }
  };
  return (
    <div data-testid="fp-view" style={art ? { backgroundImage: `url(${art})`, backgroundSize: "220px 220px", padding: "clamp(12px, 3vw, 24px)" } : undefined}>
      <span className="sr-only" data-testid={art ? "fp-wallpaper" : "fp-no-wallpaper"}>{art ? "Owner's wallpaper" : ""}</span>
      <div className="fs-panel mb-6 p-5" style={{ background: "var(--fs-card-bg, #fff)" }}>
        <div className="flex items-center gap-4">
          <MemberAvatar key={`${preview}-${m.has_photo}-${m.photo_version}`} profile={m} size={72} testid="fp-avatar" alt={`${m.display_name}'s photo`} loader={preview ? memberApi.photoBlob : () => socialApi.blob(`/members/${m.id}/photo`)} />
          <div className="min-w-0"><h1 className="fs-serif text-3xl" style={{ overflowWrap: "anywhere" }} data-testid="fp-name">{m.display_name}</h1>
            {m.username && <p className="text-xs fs-text-2" data-testid="fp-username">@{m.username}</p>}</div>
        </div>
        {data.intro && <p className="mt-4 text-sm leading-relaxed whitespace-pre-line" style={{ overflowWrap: "anywhere" }} data-testid="fp-intro">{data.intro}</p>}
      </div>
      {data.items.length === 0 ? <p className="fs-panel p-5 text-sm fs-text-2" style={{ background: "var(--fs-card-bg, #fff)" }} data-testid="fp-empty">Nothing shared here yet.</p>
        : <ul className="grid sm:grid-cols-2 gap-4" data-testid="fp-items">{data.items.map((it, i) => <ItemCard key={it.id || `${it.type}-${i}`} it={it} idx={i} preview={preview} onReport={onReport} />)}</ul>}
      {footer}
      <ReportDialog member={rep} onClose={() => setRep(null)} onSubmit={submit} />
    </div>
  );
}
